import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import {
  buildSuperAdminRegistrationActivityExportUrl,
  fetchSuperAdminRegistrationActivity,
} from "../api/superadmin";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import "./ReportsSectionCompact.css";

const PAGE_SIZES = [25, 50, 100, 250];

function formatLocalDateTime(value) {
  if (!value) return "--";
  const text = String(value);
  const match = text.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (match) return `${match[1]} ${match[2]}`;
  return text;
}

function defaultFilters() {
  const today = new Date();
  const end = today.toISOString().slice(0, 10);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 29);
  return {
    start_date: startDate.toISOString().slice(0, 10),
    end_date: end,
    platform: "",
    event_type: "",
    role: "",
    status: "",
    billing_status: "",
    source: "",
    business_id: "",
    salon_id: "",
    city: "",
    province: "",
    country: "",
    postal_code: "",
    search: "",
    page: 1,
    page_size: 50,
  };
}

function optionLabel(option) {
  return option?.label || option?.name || option?.value || "";
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
    </div>
  );
}

export default function SuperAdminRegistrationActivity() {
  const navigate = useNavigate();
  const { is_super_admin } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPages = data?.total_pages || 1;
  const items = data?.items || [];
  const options = data?.options || {};

  const queryFilters = useMemo(() => filters, [filters]);

  const loadData = async (nextFilters = queryFilters) => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSuperAdminRegistrationActivity(nextFilters);
      setData(payload);
    } catch (err) {
      setError(err?.message || "Unable to load registration activity.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (is_super_admin) {
      loadData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_super_admin]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  };

  const applyQuickFilter = (key) => {
    const next = { ...filters, page: 1 };
    if (key === "today") {
      const today = new Date().toISOString().slice(0, 10);
      next.start_date = today;
      next.end_date = today;
    } else if (key === "myboothshare") {
      next.platform = "MyBoothShare";
    } else if (key === "independent") {
      next.role = "independent_professional";
    } else if (key === "billing_missing") {
      next.billing_status = "missing";
    } else if (key === "join_pending") {
      next.event_type = "join_request_submitted";
      next.status = "pending";
    }
    setFilters(next);
    loadData(next);
  };

  const runSearch = () => loadData(filters);
  const clearFilters = () => {
    const next = defaultFilters();
    setFilters(next);
    loadData(next);
  };

  const goToPage = (page) => {
    const safePage = Math.max(1, Math.min(Number(page || 1), totalPages));
    const next = { ...filters, page: safePage };
    setFilters(next);
    loadData(next);
  };

  const exportCsv = () => {
    window.open(buildSuperAdminRegistrationActivityExportUrl(filters), "_blank", "noopener,noreferrer");
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          <ReportPageHeader title="Registration Activity" closeTo="/superadmin" />
          <div className={`space-y-6 px-6 py-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Registration Activity"
              subtitle="Review recent registration milestones across Ellyanna Booking and MyBoothShare."
              sections={[
                {
                  id: "filters",
                  title: "Filters",
                  description:
                    "Use date, platform, role, type, status, source, and location filters to narrow the activity list.",
                  anchorId: "sa-registration-filters",
                  bullets: [
                    "Search works across name, email, salon ID, business ID, and professional ID.",
                    "Use quick filters for common operational views.",
                    "Dates and times are shown in the event salon or business timezone.",
                    "Export CSV downloads the current filtered activity list.",
                  ],
                },
              ]}
              troubleshooting={[
                {
                  title: "No activity showing",
                  body: "Widen the date range or clear filters. Some older registrations may not have every source field.",
                },
              ]}
            />

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryCard label="Total events" value={data?.summary?.total_events} />
              <SummaryCard label="New users" value={data?.summary?.new_users} />
              <SummaryCard label="Ellyanna" value={data?.summary?.ellyanna_events} />
              <SummaryCard label="MyBoothShare" value={data?.summary?.myboothshare_events} />
              <SummaryCard label="Billing accounts" value={data?.summary?.billing_accounts} />
            </div>

            <div id="sa-registration-filters" className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="mb-4 flex flex-wrap gap-2">
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("today")}>
                  New today
                </button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("myboothshare")}>
                  MyBoothShare
                </button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("independent")}>
                  Independent professionals
                </button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("billing_missing")}>
                  Billing missing
                </button>
                <button type="button" className={BTN_OUTLINE} onClick={() => applyQuickFilter("join_pending")}>
                  Join requests pending
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <label className={LABEL_TEXT}>Start date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.start_date} onChange={(event) => updateFilter("start_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>End date</label>
                  <input className={`${FIELD_CLASS} w-full`} type="date" value={filters.end_date} onChange={(event) => updateFilter("end_date", event.target.value)} />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Platform</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.platform} onChange={(event) => updateFilter("platform", event.target.value)}>
                    <option value="">All platforms</option>
                    {(options.platforms || []).map((option) => (
                      <option key={option.value} value={option.value}>{optionLabel(option)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Type</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.event_type} onChange={(event) => updateFilter("event_type", event.target.value)}>
                    <option value="">All types</option>
                    {(options.event_types || []).map((option) => (
                      <option key={option.value} value={option.value}>{optionLabel(option)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Role</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.role} onChange={(event) => updateFilter("role", event.target.value)}>
                    <option value="">All roles</option>
                    {(options.roles || []).map((option) => (
                      <option key={option.value} value={option.value}>{optionLabel(option)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Status</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
                    <option value="">All statuses</option>
                    {(options.statuses || []).map((option) => (
                      <option key={option.value} value={option.value}>{optionLabel(option)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Billing status</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.billing_status} onChange={(event) => updateFilter("billing_status", event.target.value)}>
                    <option value="">All billing statuses</option>
                    {(options.billing_statuses || []).map((option) => (
                      <option key={option.value} value={option.value}>{optionLabel(option)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Source</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.source} onChange={(event) => updateFilter("source", event.target.value)}>
                    <option value="">All sources</option>
                    {(options.sources || []).map((option) => (
                      <option key={option.value} value={option.value}>{optionLabel(option)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Business</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.business_id} onChange={(event) => updateFilter("business_id", event.target.value)}>
                    <option value="">All businesses</option>
                    {(options.businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>{business.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Salon</label>
                  <select className={`${FIELD_CLASS} w-full`} value={filters.salon_id} onChange={(event) => updateFilter("salon_id", event.target.value)}>
                    <option value="">All salons</option>
                    {(options.salons || []).map((salon) => (
                      <option key={salon.id} value={salon.id}>{salon.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>City</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.city} onChange={(event) => updateFilter("city", event.target.value)} placeholder="City" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Province / State</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.province} onChange={(event) => updateFilter("province", event.target.value)} placeholder="Province or state" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Country</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.country} onChange={(event) => updateFilter("country", event.target.value)} placeholder="Country" />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Postal / ZIP</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.postal_code} onChange={(event) => updateFilter("postal_code", event.target.value)} placeholder="Postal or ZIP" />
                </div>
                <div className="xl:col-span-2">
                  <label className={LABEL_TEXT}>Search</label>
                  <input className={`${FIELD_CLASS} w-full`} value={filters.search} onChange={(event) => updateFilter("search", event.target.value)} placeholder="Name, email, salon ID, business ID, professional ID" />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-3">
                <button type="button" className={BTN_OUTLINE} onClick={clearFilters}>Clear</button>
                <button type="button" className={BTN_OUTLINE} onClick={exportCsv}>Export CSV</button>
                <button type="button" className={BTN_PRIMARY} onClick={runSearch} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    {["Date", "Platform", "Type", "Name", "Role", "Status", "Billing", "Business", "Salon", "Location", "Actions"].map((header) => (
                      <th key={header} className="px-4 py-3 text-left font-semibold">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-500">Loading registration activity...</td></tr>
                  ) : items.length ? (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 text-slate-700">
                          <div>{formatLocalDateTime(row.occurred_at_local || row.occurred_at)}</div>
                          <div className="text-xs text-slate-500">{row.timezone || "--"}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{row.platform || "--"}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">{row.event_label || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="font-semibold text-slate-900">{row.display_name || "--"}</div>
                          <div className="text-xs text-slate-500">{row.email || "--"}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{row.role_label || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">{row.status || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">{row.billing_status || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">{row.business_name || "--"}{row.business_id ? ` (ID ${row.business_id})` : ""}</td>
                        <td className="px-4 py-3 text-slate-700">{row.salon_name || "--"}{row.salon_id ? ` (ID ${row.salon_id})` : ""}</td>
                        <td className="px-4 py-3 text-slate-700">{[row.city, row.province, row.country].filter(Boolean).join(", ") || "--"}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <button type="button" className={BTN_OUTLINE} onClick={() => navigate("/superadmin")}>Open Console</button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-500">No registration activity found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                Showing {items.length ? (filters.page - 1) * filters.page_size + 1 : 0}
                {"-"}
                {Math.min(filters.page * filters.page_size, data?.total || 0)} of {data?.total || 0} events
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select className={FIELD_CLASS} value={filters.page_size} onChange={(event) => {
                  const next = { ...filters, page: 1, page_size: Number(event.target.value) };
                  setFilters(next);
                  loadData(next);
                }}>
                  {PAGE_SIZES.map((size) => <option key={size} value={size}>{size} per page</option>)}
                </select>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(1)} disabled={filters.page <= 1}>First</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page - 1)} disabled={filters.page <= 1}>Previous</button>
                <span className="text-sm font-semibold text-slate-700">Page {filters.page} of {totalPages}</span>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(filters.page + 1)} disabled={filters.page >= totalPages}>Next</button>
                <button type="button" className={BTN_OUTLINE} onClick={() => goToPage(totalPages)} disabled={filters.page >= totalPages}>Last</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
